import clsx from 'clsx';
import { HTMLAttributes } from 'react';

import countryCodesAlpha3 from './country-iso-alpha-three.json';
import countryCodes from './country-iso-codes.json';
import styles from './FlagIcon.module.scss';
import regionalCodes from './regional-codes.json';
import { convertCountryCodeToAlpha2, isRegionalCode } from './utils';

export interface FlagIconProps extends HTMLAttributes<HTMLSpanElement> {
  countryCode?: string;
  variant?: 'default' | 'rounded' | 'circular';
  customSize?: number;
}

// Direct dependency to "https://cdn.jsdelivr.net/npm/flag-icons/css/flag-icons.min.css"
export const FlagIcon = ({
  countryCode = '',
  variant = 'default',
  customSize = 14,
  ...rest
}: FlagIconProps) => {
  const { className, style, ...otherProps } = rest;
  const countryCodeUpperCase = countryCode.toUpperCase();

  if (!countryCode) {
    return null;
  }

  const isValidCode =
    countryCodesAlpha3.includes(countryCodeUpperCase) ||
    countryCodes.includes(countryCodeUpperCase) ||
    regionalCodes.includes(countryCodeUpperCase);

  if (!isValidCode) {
    return (
      <span
        className={clsx(styles.fallback, className)}
        style={{ minHeight: `${customSize}px` }}
        data-testid="fallback"
        {...rest}
      >
        {countryCodeUpperCase}
      </span>
    );
  }

  const alpha2Code = convertCountryCodeToAlpha2(countryCodeUpperCase);

  if (!alpha2Code || isRegionalCode(countryCodeUpperCase)) {
    return (
      <span
        className={clsx(styles.fallback, className)}
        style={{ minHeight: `${customSize}px` }}
        data-testid="fallback"
        {...rest}
      >
        {countryCodeUpperCase}
      </span>
    );
  }

  const flagClasses = clsx(
    styles.flag,
    'fi',
    `fi-${alpha2Code.toLowerCase()}`,
    variant === 'circular' && 'fis',
    variant !== 'default' && styles[variant],
    className
  );

  const flagStyle = {
    height: `${customSize}px`,
    width: `${variant === 'circular' ? customSize : customSize * 1.5}px`,
  };

  return (
    <span
      className={flagClasses}
      style={flagStyle}
      data-country={countryCodeUpperCase}
      {...otherProps}
    />
  );
};
